export function camelCaseToTitle(camelCase: string): string {
    if (!camelCase) return '';

    const withSpaces = camelCase
        // Convert underscores into spaces and collapse runs
        .replace(/[\s_]+/g, ' ')

        // Handle acronyms: "XMLParser" -> "XML Parser"
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')

        // Handle camelCase: "camelCase" -> "camel Case"
        .replace(/([a-z\d])([A-Z])/g, '$1 $2');

    // Capitalize first letter
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

/**
 * Trims at most one slash from the beginning and the end of a string.
 */
export function trimBoundarySlashes(s: string): string {
    if (s === '/') return '';
    const start = s.startsWith('/') ? 1 : 0;
    const end = s.endsWith('/') ? s.length - 1 : s.length;
    return s.substring(start, end);
}

/**
 * Splits a Firestore path into an Array of [collection, document] pairs.
 * For example, an input of
 *   `"states/MI/cities/Ann_Arbor"`
 * returns
 *   `[["states", "MI"], ["cities", "Ann_Arbor"]]`.
 *
 * If the path has an odd number of segments, it represents a collection,
 * and index 1 of the last tuple will be undefined.
 *
 * Up to one lead and one trailing slash will be trimmed from the input as
 * a convenience. Inputs of `""` (empty string), `"/"` (single slash), and
 * `"//"` all result in an empty array.
 *
 * Names are not validated. A double slash occurring in the middle of the
 * input string will result in an empty string for a part's collection or
 * document name.
 */
export function splitFirestorePath(
    firestorePath: string
): Array<[string, string | undefined]> {
    firestorePath = trimBoundarySlashes(firestorePath);
    if (firestorePath === '') return [];

    const parts = firestorePath.split('/');
    const qParts: Array<[string, string | undefined]> = [];
    for (let i = 0; i < parts.length; i += 2) {
        qParts.push([parts[i], parts[i + 1]]);
    }
    return qParts;
}

/**
 * Takes a filename in the form of a string and returns the file extension.
 * @param {string} filename - The filename
 * @returns {string} The file extension
 * */
export function getFilenameExtension(filename: string) {
    const extension = filename.match(/\.[0-9a-z]{1,5}$/i);
    if (extension) return extension[0];
}

/**
 * Returns the part of a slash-delimited path after its last `/`, or the whole
 * string if it contains none. As with `split('/').pop()`, a trailing slash
 * gives `''`.
 */
export function lastPathSegment(path: string): string {
    return path.slice(path.lastIndexOf('/') + 1);
}

export function removeFilenameExtension(str: string) {
    return str.replace(/\.[^/.]+$/, '');
}

/**
 * Splits a host:port string into a host and port number.
 *
 * Note that this function requires a port number.
 *
 * @param {string} hostPort The host:port string to split
 * @returns {[string, number]} The host and port number
 **/
export function splitHostPort(hostPort: string): [string, number] {
    const c = hostPort.lastIndexOf(':');

    let host = '';
    let portStr = '';

    if (c !== -1) {
        host = hostPort.slice(0, c);
        portStr = hostPort.slice(c + 1);
    }

    if (!host) throw new Error('Missing hostname');
    if (!portStr) throw new Error('Missing port');

    if (!/^\d+$/.test(portStr)) {
        throw new Error('Port not an integer');
    }

    const portNum = +portStr;
    if (portNum < 0 || portNum > 0xffff) {
        throw new Error('Port number out of range');
    }

    return [host, portNum];
}

export function encodeURIPathname(pathname: string): string {
    return pathname
        .split('/')
        .map((p) => encodeURIComponent(p))
        .join('/');
}

/**
 * Removes extra spaces from the beginning and end of the string, and extra spaces between words.
 * @example
 * ```
 * normalizeSpace(' This  is a string with  extra spaces ');
 * // returns 'This is a string with extra spaces'
 * ```
 * @param str The string to remove extra spaces from.
 * @returns The string with extra spaces removed.
 * */
export function normalizeSpace(str: string) {
    return str.trim().replace(/\s+/g, ' ');
}

/**
 * Like `normalizeSpace`, but permits at most one trailing space. This
 * allows for use as a per-keypress form normalizer, because trimming
 * trailing space after each change would prevent the user from typing
 * spaces normally.
 *
 * Code that receives the form values upon submission will likely still
 * want to perform a trimEnd on the received value.
 */
export function normalizeInputSpace(str: string) {
    return str.trimStart().replace(/\s+/g, ' ');
}

/**
 * Strips any character that is not alphanumeric, a plain space, or one of the
 * explicitly allowed symbols: `% - _ ! @ # $ ( ) + =`
 *
 * @example
 * ```
 * removeSpecialCharacters('hello, world! (v2.0)')
 * // returns 'hello world! (v20)'
 * // comma, period removed; exclamation mark, parens, space kept
 * ```
 * @param str The string to strip.
 * @returns The string with all disallowed characters removed.
 */
export function removeSpecialCharacters(str: string) {
    return str.replace(/[^a-zA-Z0-9\x20%_!@#$()+=-]+/g, '');
}

// NOTE: stripSlash and slashJoin are also present in adhublib. Long term
//  goal: add adhublib as a dependency
function stripSlash(part: string, index: number, length: number): string {
    // if not the first part, strip a leading slash if present
    if (index > 0 && part.startsWith('/')) {
        part = part.slice(1);
    }

    // if not the last part, strip a trailing slash if present
    if (index < length - 1 && part.endsWith('/')) {
        part = part.slice(0, -1);
    }

    return part;
}

/**
 * Joins two or more strings together, ensuring a forward slash is inserted
 * between them.
 *
 * Note that this function strips at most one slash from the start/end of
 * input strings. If the input strings contain runs of repeated slashes,
 * the output of this function will also contain them.
 */
export function slashJoin(...parts: string[]): string {
    if (parts.length === 0) return '';
    if (parts.length === 1) return `${parts[0]}`;
    return parts
        .map((part, index) => stripSlash(`${part}`, index, parts.length))
        .join('/');
}

export function stripOptionalSuffix(subject: string, suffix: string): string {
    return subject.endsWith(suffix)
        ? subject.slice(0, -suffix.length)
        : subject;
}

/**
 * Splits a string at CRLF or LF character sequences.
 */
export function splitLines(text: string): string[] {
    return text.split(/\r\n|\n/);
}

/**
 * Whether `haystack` contains `needle`, ignoring case.
 *
 * Accepts any type of parameter for ease of use against unvalidated data;
 * can only ever return `true` iff both arguments are strings.
 */
export function includesNoCase(haystack: unknown, needle: unknown) {
    // Folds upward, and not with the locale-aware variant. Both choices matter:
    //
    // - Upper folds strictly more pairs together than lower. 'ß' uppercases to
    //   'SS', and both Greek sigmas ('σ' and final 'ς') uppercase to 'Σ'. Going
    //   the other way, 'SS' lowercases to 'ss' while 'ß' stays put, and 'Σ'
    //   lowercases only to 'σ', so those pairs never meet.
    // - toLocaleUpperCase would fold using the *host's* locale, so the same input
    //   would match or not depending on the user's machine. Turkish is the usual
    //   casualty: there 'i' uppercases to dotted 'İ', so searching "i" would stop
    //   matching "I".
    //
    // Not handled, and out of scope until something needs it: diacritics are
    // compared as written, so 'é' does not match a decomposed 'e' + U+0301. That
    // wants normalize() on both sides rather than a different case operation.
    return (
        typeof needle === 'string' &&
        typeof haystack === 'string' &&
        haystack.toUpperCase().includes(needle.toUpperCase())
    );
}

export function pluralEnglish(
    count: number,
    singular: string,
    plural?: string
) {
    if (!plural) plural = `${singular}s`;
    return count === 1 ? singular : plural;
}
